import { minBy, orderBy } from 'lodash'
import moment, { Moment } from 'moment'

import type ContractVersion from 'modules/contracts/store/models/ContractVersion'

type OrderType = 'desc' | 'asc'

class Contract {
  startsOn: Moment
  endsOn: Moment | null
  versions: ContractVersion[]

  constructor(startsOn: string | Moment) {
    this.startsOn = moment(startsOn)
    this.endsOn = null
    this.versions = []
  }

  sortedVersions(order: OrderType = 'desc') {
    return orderBy(this.versions, ['effectiveOn'], [order])
  }

  /**
   * We need to calculate the min effective on until we have some date validation.
   * If we don't do this, we can have inconsistencies with contracts that
   * has a start on date lower than the first effective on date.
   **/
  effectiveOn() {
    const minEffectiveOnVersion = minBy(this.versions, 'effectiveOn')
    if (!minEffectiveOnVersion?.effectiveOn) return null
    if (!minEffectiveOnVersion.effectiveOn.isValid()) return null

    return minEffectiveOnVersion?.effectiveOn
  }

  pushVersion(version: ContractVersion) {
    this.versions.push(version)
  }

  includesVersion(version: ContractVersion): boolean {
    return !!this.versions.find(({ id }) => id === version.id)
  }

  formattedStartOn(format = 'MMM Do YYYY'): string {
    return this.startsOn.format(format)
  }

  hasEndDate(): boolean {
    return this.endsOn !== null
  }

  isOngoing(): boolean {
    const now = moment()
    const targetDate = this.effectiveOn() || this.startsOn
    return !targetDate.isAfter(now) && !this.hasExpired()
  }

  hasExpired(): boolean {
    const now = moment()
    return this.endsOn !== null && this.endsOn.isBefore(now)
  }

  isUpcoming(): boolean {
    const now = moment()
    const targetDate = this.effectiveOn() ?? this.startsOn
    return targetDate.isAfter(now)
  }
}

export default Contract
